import { isClientTimeout } from './getErrorCode';
/**
 * Every kind, for tests that must cover the whole space rather than the statuses
 * someone happened to think of.
 *
 * Built from a `Record<ErrorKind, true>` rather than written as an array: an
 * `ErrorKind[]` would accept any SUBSET, so omitting a member would compile
 * silently — and a kind missing from here is skipped by every assertion in
 * `errorMessageCoverage.test.ts`, which is exactly the drift this file exists to
 * stop. The Record makes a missing member a compile error.
 */
var EVERY_KIND = {
    network: true,
    timeout: true,
    validation: true,
    unauthenticated: true,
    forbidden: true,
    notFound: true,
    conflict: true,
    unprocessable: true,
    server: true,
    unknown: true,
};
export var ALL_ERROR_KINDS = Object.keys(EVERY_KIND);
/**
 * Guards the exhaustiveness of a `switch` over {@link ErrorKind}. A new member
 * reaches each formatter's `default:` branch as a type error until that
 * formatter answers for it.
 */
export var assertNever = function (kind) {
    throw new Error("Unhandled error kind: ".concat(String(kind)));
};
export var getErrorKind = function (info) {
    var status = info.status, timedOut = info.timedOut;
    // CZNA-1625 — a client timeout has no response either; it must be told apart first.
    if (timedOut)
        return 'timeout';
    // `getErrorInfo` returns `{}` when the thrown value carries no response,
    // which is how a network/abort failure reaches us.
    if (status === undefined)
        return 'network';
    if (status === 400)
        return 'validation';
    if (status === 401)
        return 'unauthenticated';
    if (status === 403)
        return 'forbidden';
    if (status === 404)
        return 'notFound';
    if (status === 409)
        return 'conflict';
    if (status === 422)
        return 'unprocessable';
    // CZNA-1625 — the gateway stopped waiting for the module; the module may still have written.
    if (status === 504)
        return 'timeout';
    if (status >= 500)
        return 'server';
    return 'unknown';
};
/**
 * CZNA-1625 — whether a thrown value is a `timeout` WITHOUT reading its body.
 *
 * The save paths decide on a timeout before they format the message, and `getErrorInfo`
 * consumes the response body the formatter still needs (ky's `json()` reads once). The
 * decision itself is still `getErrorKind`'s — this only feeds it what is readable for free.
 */
export var isTimeoutError = function (error) {
    var _a;
    return getErrorKind({
        status: (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status,
        timedOut: isClientTimeout(error),
    }) === 'timeout';
};
